<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="overlay"
        :class="{ 'overlay--full': size === 'full' }"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @mousedown.self="dismiss"
      >
        <Transition name="slide-up" appear>
          <div ref="panel" class="modal" :class="`modal--${size}`" tabindex="-1">
            <header class="modal__head">
              <h3 class="modal__title">{{ title }}</h3>
              <UiClose @click="close" />
            </header>

            <div class="modal__body"><slot /></div>

            <footer v-if="$slots.footer" class="modal__foot"><slot name="footer" /></footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

import UiClose from './UiClose.vue'

const props = defineProps({
  // Not bound means open. A screen that mounts a dialog with `v-if` has already
  // decided to show it, and that is how most of them are written: the modal is
  // the thing the row click created. Binding it instead (`v-model`, or
  // `:model-value` + `@update:model-value`) keeps the component mounted and
  // drives it from outside, which is what a modal that lives in a child
  // component with its own `modelValue` prop needs. Both are supported; the
  // default used to be `false`, and every dialog written the first way opened
  // nothing at all when clicked.
  modelValue: { type: Boolean, default: true },
  title: { type: String, default: '' },
  size: { type: String, default: 'md' },   // sm | md | lg | full
  persistent: Boolean,                      // ignore Escape and backdrop clicks
})
const emit = defineEmits(['update:modelValue', 'close'])

const panel = ref(null)

/** Asked for: the cross. Always obeyed. */
function close() {
  emit('update:modelValue', false)
  emit('close')
}

/** Not asked for: the backdrop and Escape. */
function dismiss() {
  // Persistent modals are for forms with unsaved data: losing a long entry to a
  // stray backdrop click is worse than one extra click to close. The cross is
  // not one of those accidents, though — it used to go through here too, which
  // left the "new user" dialog with a cross that did nothing at all.
  if (props.persistent) return
  close()
}

function onKeydown(event) {
  if (event.key === 'Escape') dismiss()
}

async function arm() {
  document.addEventListener('keydown', onKeydown)
  // The page behind must not scroll while a modal is up.
  document.body.style.overflow = 'hidden'
  await nextTick()
  // Focus the dialog itself rather than the first control in it. That is
  // enough for a screen reader to announce where it now is and for Escape to
  // reach us, without putting a focus ring on the close cross — which is what
  // the first control happened to be — or opening a phone keyboard over a
  // form the user has not decided to fill in yet.
  panel.value?.focus()
}

function disarm() {
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
}

watch(() => props.modelValue, (open) => (open ? arm() : disarm()))

// Also on mount, because a dialog mounted open never changes `modelValue`: the
// watcher would never run, and Escape, the scroll lock and the focus would all
// be missing from exactly the dialogs written that way.
onMounted(() => { if (props.modelValue) arm() })

onUnmounted(disarm)
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  /* Lo que se ve y no la ventana (ui-kit utils/viewport.js). Teleportado a
     <body> queda fuera del armazón, y con el teclado fuera en iOS `inset: 0`
     medía también lo que hay debajo de las teclas: el pie con «Guardar» se
     quedaba tapado por ellas. */
  top: var(--viewport-top, 0px);
  bottom: auto;
  height: var(--app-height, 100dvh);
  /*
    Above the assistant's edge tab (130) and its panel (140), which used to draw
    straight over an open dialog, and above the update notice (180) that would
    otherwise land on the sheet's own buttons. Still under the select's list
    (190) and the toasts (200), both of which belong on top of a dialog.
  */
  z-index: 185;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal {
  width: 100%;
  /* dvh, not vh: on a phone `100vh` is the height the window has with the
     browser's address bar hidden, so a vh-sized dialog is taller than the
     screen for as long as that bar is showing. */
  max-height: calc(var(--app-height, 100dvh) - 2 * var(--space-8));
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
/* It is only focusable so that focus has somewhere to land; it is not a control
   and must not draw a ring. */
.modal:focus { outline: none; }
.modal--sm { max-width: 400px; }
.modal--md { max-width: 560px; }
.modal--lg { max-width: 820px; }
/* ── A screen, not a dialog ──────────────────────────────────────────────────
   `full` is for something that stopped being a question and became a place: a
   catalogue with a live preview beside it, a list long enough to scroll. It
   takes the whole viewport with no margin, no corners and no backdrop showing
   round the edges, because at this size every one of those is width taken from
   the thing somebody opened it to read. It was a 1400px card in a dark frame,
   and on a wide monitor that frame was three hundred pixels of nothing on each
   side of a description that had to wrap. */
.overlay--full {
  padding: 0;
  background: var(--color-surface);
  backdrop-filter: none;
}
.modal--full {
  max-width: none;
  height: var(--app-height, 100dvh);
  max-height: var(--app-height, 100dvh);
  border-radius: 0;
  box-shadow: none;
}
/* Its head and foot line up with the panes below them rather than with a
   dialog's tighter gutter. */
.modal--full .modal__head,
.modal--full .modal__foot { padding-inline: var(--space-6); }

.modal__head {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.modal__title { flex: 1; font-size: var(--text-lg); font-weight: 600; }

.modal__body { padding: var(--space-5); overflow-y: auto; }
/* The full-size dialog hands scrolling to whatever is inside it: two panes that
   scroll together would put the preview out of sight the moment somebody looks
   down the list, which is the one thing it is there for. */
.modal--full .modal__body { flex: 1; min-height: 0; padding: 0; overflow: hidden; }

.modal__foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-2);
}

/* ── Phone ───────────────────────────────────────────────────────────────────
   A sheet from the bottom instead of a card in the middle. It is where a thumb
   already is, it is what every application on the device does, and it gives the
   form the full width of the screen instead of the full width minus 32px.
*/
@media (max-width: 640px) {
  .overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal {
    max-width: none;
    max-height: calc(var(--app-height, 100dvh) * 0.92);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }

  /* Except the full one, which is already the screen. */
  .modal--full {
    max-height: var(--app-height, 100dvh);
    border-radius: 0;
  }
  .modal--full .modal__head,
  .modal--full .modal__foot { padding-inline: var(--space-4); }

  .modal__body { padding: var(--space-4); }

  .modal__foot {
    padding: var(--space-3) var(--space-4);
    /* Clear of the home indicator, which sits on top of anything down here. */
    padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom));
  }
  /* Two buttons, half the width each: no aiming at a 90px target in the corner
     of a screen you are holding one-handed. */
  .modal__foot > * { flex: 1; }

  /* It arrives from the edge it is attached to, rather than fading up ten
     pixels the way the centred dialog does. */
  .slide-up-enter-from,
  .slide-up-leave-to { transform: translateY(100%); opacity: 1; }
}
</style>

<style>
/* Con el teclado fuera, el pie se apoya en las teclas: el hueco del indicador
   de inicio (que el teclado tapa) era una franja vacía entre «Guardar» y ellas.
   Sin scope a propósito: `:global(html.x) .y` en un bloque scoped se compila a
   `html.x` a secas y se lleva por delante el resto del selector. */
@media (max-width: 640px) {
  html.keyboard-open .modal__foot { padding-bottom: var(--space-3); }
}
</style>
